import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { mcpApi } from '@/api/mcp.api';
import {
  fromMCP,
  isMCPInstanceFormValid,
  toMCP,
  type MCPInstanceFormState,
} from '@/pages/mcp/mcp-instance-form.utils';
import { MCPInstanceForm } from '@/pages/mcp/MCPInstanceForm';

export default function EditMCPInstancePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MCPInstanceFormState | null>(null);

  const { data: mcp, isLoading } = useQuery({
    queryKey: ['mcp', id],
    queryFn: () => mcpApi.getMCP(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (mcp) setForm(fromMCP(mcp));
  }, [mcp]);

  const updateMutation = useMutation({
    mutationFn: (f: MCPInstanceFormState) => mcpApi.updateMCP(f.id!, toMCP(f)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp', id] });
      queryClient.invalidateQueries({ queryKey: ['mcps'] });
      navigate(`/plugins/mcps/instances/${id}`);
    },
  });

  if (isLoading || !form) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/plugins/mcps/instances/${id}`)}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">编辑 MCP 实例</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{form.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/plugins/mcps/instances/${id}`)}>
            取消
          </Button>
          <Button
            disabled={!isMCPInstanceFormValid(form) || updateMutation.isPending}
            onClick={() => updateMutation.mutate(form)}
          >
            {updateMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}保存修改
          </Button>
        </div>
      </div>

      <MCPInstanceForm form={form} onChange={setForm} />
    </div>
  );
}
